// Centralised image resolution + per-type fallback chain.
// Used by all card and detail components to guarantee an image is always shown.
//
// Each content type has a *pool* of 20+ curated editorial images. The image
// shown for a given entity is picked deterministically from the pool using a
// hash of the entity id, so:
//   - the same entry always shows the same image (no flicker between renders);
//   - different entries on the same page show *different* generic images.

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const u = (id) => `https://images.unsplash.com/photo-${id}?w=1400&q=80&auto=format&fit=crop`;

// Per-type curated pools (20+ images each). Themes:
//   news      -> editorial / newsroom / business reading
//   insight   -> analysis, charts, deep-dive, research
//   alert     -> monitoring, signals, screens, dashboards
//   event     -> conferences, meetings, panels, audiences
//   job       -> hiring, teams at work, modern offices
//   tutorial  -> learning, code, workshop, hands-on
//   ecosystem -> partnerships, networking, business community
export const FALLBACK_POOLS = {
  news: [
    u("1495020689067-958852a7765e"),
    u("1504711434969-e33886168f5c"),
    u("1585829365295-ab7cd400c167"),
    u("1586892477838-2b96e85e0f96"),
    u("1576091160550-2173dba999ef"),
    u("1518186285589-2f7649de83e0"),
    u("1454165804606-c3d57bc86b40"),
    u("1432888622747-4eb9a8efeb07"),
    u("1497032628192-86f99bcd76bc"),
    u("1486312338219-ce68d2c6f44d"),
    u("1542435503-956c469947f6"),
    u("1495465798138-718f86d1a4bc"),
    u("1542744173-8e7e53415bb0"),
    u("1551434678-e076c223a692"),
    u("1499750310107-5fef28a66643"),
    u("1551836022-d5d88e9218df"),
    u("1543286386-2e659306cd6c"),
    u("1521737711867-e3b97375f902"),
    u("1556761175-5973dc0f32e7"),
    u("1450101499163-c8848c66ca85"),
  ],
  insight: [
    u("1454165804606-c3d57bc86b40"),
    u("1543286386-2e659306cd6c"),
    u("1518186285589-2f7649de83e0"),
    u("1460925895917-afdab827c52f"),
    u("1551288049-bebda4e38f71"),
    u("1486312338219-ce68d2c6f44d"),
    u("1542744173-8e7e53415bb0"),
    u("1495465798138-718f86d1a4bc"),
    u("1551434678-e076c223a692"),
    u("1499750310107-5fef28a66643"),
    u("1576091160550-2173dba999ef"),
    u("1542626991-cbc4e32524cc"),
    u("1497032628192-86f99bcd76bc"),
    u("1432888622747-4eb9a8efeb07"),
    u("1542435503-956c469947f6"),
    u("1450101499163-c8848c66ca85"),
    u("1551836022-d5d88e9218df"),
    u("1556761175-5973dc0f32e7"),
    u("1494178270175-e96de2971df9"),
    u("1554224155-8d04cb21cd6c"),
  ],
  alert: [
    u("1611974789855-9c2a0a7236a3"),
    u("1605792657660-596af9009e82"),
    u("1581090464777-f3220bbe1b8b"),
    u("1518770660439-4636190af475"),
    u("1485827404703-89b55fcc595e"),
    u("1496247749665-49cf5b1022e9"),
    u("1485965120184-e220f721d03e"),
    u("1554224155-8d04cb21cd6c"),
    u("1531297484001-80022131f5a1"),
    u("1542744094-3a31f272c490"),
    u("1551434678-e076c223a692"),
    u("1499750310107-5fef28a66643"),
    u("1576091160550-2173dba999ef"),
    u("1543286386-2e659306cd6c"),
    u("1486312338219-ce68d2c6f44d"),
    u("1495465798138-718f86d1a4bc"),
    u("1542744173-8e7e53415bb0"),
    u("1454165804606-c3d57bc86b40"),
    u("1518186285589-2f7649de83e0"),
    u("1551836022-d5d88e9218df"),
  ],
  event: [
    u("1556761175-5973dc0f32e7"),
    u("1505373877841-8d25f7d46678"),
    u("1540575467063-178a50c2df87"),
    u("1511795409834-ef04bbd61622"),
    u("1517048676732-d65bc937f952"),
    u("1559223607-a43c990c692c"),
    u("1591115765373-5207764f72e7"),
    u("1531058020387-3be344556be6"),
    u("1492684223066-81342ee5ff30"),
    u("1475721027785-f74eccf877e2"),
    u("1576091160399-112ba8d25d1d"),
    u("1542744095-fcf48d80b0fd"),
    u("1540304453527-62f979142a17"),
    u("1591115765373-5207764f72e7"),
    u("1517245386807-bb43f82c33c4"),
    u("1543269865-cbf427effbad"),
    u("1551836022-d5d88e9218df"),
    u("1518186285589-2f7649de83e0"),
    u("1431540015161-0bf868a2d407"),
    u("1530099486328-e021101a494a"),
  ],
  job: [
    u("1521737711867-e3b97375f902"),
    u("1521737604893-d14cc237f11d"),
    u("1573496359142-b8d87734a5a2"),
    u("1556761175-5973dc0f32e7"),
    u("1543269865-cbf427effbad"),
    u("1497032628192-86f99bcd76bc"),
    u("1542744095-fcf48d80b0fd"),
    u("1556761175-5973dc0f32e7"),
    u("1551836022-d5d88e9218df"),
    u("1542744094-3a31f272c490"),
    u("1517245386807-bb43f82c33c4"),
    u("1543269865-cbf427effbad"),
    u("1531058020387-3be344556be6"),
    u("1486312338219-ce68d2c6f44d"),
    u("1497215842964-222b430dc094"),
    u("1542744173-8e7e53415bb0"),
    u("1542435503-956c469947f6"),
    u("1454165804606-c3d57bc86b40"),
    u("1542744095-fcf48d80b0fd"),
    u("1559223607-a43c990c692c"),
  ],
  tutorial: [
    u("1454165804606-c3d57bc86b40"),
    u("1517694712202-14dd9538aa97"),
    u("1531403009284-440f080d1e12"),
    u("1542831371-29b0f74f9713"),
    u("1556761175-5973dc0f32e7"),
    u("1551434678-e076c223a692"),
    u("1542744173-8e7e53415bb0"),
    u("1486312338219-ce68d2c6f44d"),
    u("1518186285589-2f7649de83e0"),
    u("1497032628192-86f99bcd76bc"),
    u("1499750310107-5fef28a66643"),
    u("1543286386-2e659306cd6c"),
    u("1576091160550-2173dba999ef"),
    u("1495465798138-718f86d1a4bc"),
    u("1542435503-956c469947f6"),
    u("1551836022-d5d88e9218df"),
    u("1450101499163-c8848c66ca85"),
    u("1503676260728-1c00da094a0b"),
    u("1531403009284-440f080d1e12"),
    u("1517048676732-d65bc937f952"),
  ],
  ecosystem: [
    u("1554469384-e58fac16e23a"),
    u("1521737711867-e3b97375f902"),
    u("1556761175-5973dc0f32e7"),
    u("1542744095-fcf48d80b0fd"),
    u("1573496359142-b8d87734a5a2"),
    u("1497215842964-222b430dc094"),
    u("1551434678-e076c223a692"),
    u("1521737604893-d14cc237f11d"),
    u("1431540015161-0bf868a2d407"),
    u("1559223607-a43c990c692c"),
    u("1531058020387-3be344556be6"),
    u("1543269865-cbf427effbad"),
    u("1542744094-3a31f272c490"),
    u("1517245386807-bb43f82c33c4"),
    u("1454165804606-c3d57bc86b40"),
    u("1486312338219-ce68d2c6f44d"),
    u("1497032628192-86f99bcd76bc"),
    u("1576091160550-2173dba999ef"),
    u("1551836022-d5d88e9218df"),
    u("1556761175-5973dc0f32e7"),
  ],
};

// Global last-resort fallback (Dataleo neutral image).
export const GLOBAL_FALLBACK = u("1494412519320-aa613dfb7738");

// Deterministic hash → integer ≥ 0. Same input always returns same output.
const hashSeed = (s) => {
  const str = String(s || "");
  let h = 5381;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) + h) + str.charCodeAt(i); // h * 33 + c
    h |= 0;
  }
  return Math.abs(h);
};

// Pick one image from the type pool, deterministically based on `seed`.
// Without a seed, returns the first image of the pool (legacy behaviour).
export const fallbackForType = (type = "news", seed = "") => {
  const pool = FALLBACK_POOLS[type] || FALLBACK_POOLS.news;
  if (!pool || pool.length === 0) return GLOBAL_FALLBACK;
  if (!seed) return pool[0];
  return pool[hashSeed(seed) % pool.length];
};

// Back-compat: legacy callers that imported FALLBACK_BY_TYPE as a string map
// (one image per type) keep working. Each value here is just the pool's first
// entry; entity-aware callers should switch to `fallbackForType(type, id)` or
// `pickEntityImage(entity, type)`.
export const FALLBACK_BY_TYPE = Object.fromEntries(
  Object.entries(FALLBACK_POOLS).map(([k, v]) => [k, v[0]])
);

// Normalize a stored URL: `/api/uploads/...` → absolute backend URL.
export const resolveImg = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (trimmed.startsWith("/api/")) return `${BACKEND}${trimmed}`;
  if (trimmed.startsWith("//")) return `https:${trimmed}`;
  return trimmed;
};

// Stock photos the legacy backend `backfill_images` job wrote into news rows
// with no image. Treated as "no image" so each article gets its own seeded
// pool picture instead of the same repeated default.
const PLACEHOLDER_IDS = [
  "1454165804606-c3d57bc86b40", "1529107386315-e1a2ed48a620", "1581091226825-a6a2a5aee158",
  "1601584115197-04ecc0da31d7", "1494412519320-aa613dfb7738", "1450101499163-c8848c66ca85",
  "1551288049-bebda4e38f71", "1521737604893-d14cc237f11d", "1611974789855-9c2a0a7236a3",
];

export const isPlaceholderImage = (url) =>
  typeof url === "string" && url.includes("images.unsplash.com") && url.endsWith("?w=1200&q=80")
  && PLACEHOLDER_IDS.some((id) => url.includes(id));

// Themed pools for news without an editorial image, keyed by subject.
const TOPIC_POOLS = {
  planning: ["1551288049-bebda4e38f71", "1526628953301-3e589a6a8b74", "1560221328-12fe60f83ab8", "1591696205602-2f950c417cb9", "1557804506-669a67965ba0", "1532622785990-d2c36a76f5a6", "1512758017271-d7b84c2113f1", "1460925895917-afdab827c52f", "1543286386-2e659306cd6c"],
  inventory: ["1672552226380-486fe900b322", "1644079446600-219068676743", "1587293852726-70cdb56c2866", "1553413077-190dd305871c", "1620388640785-892616248ec8", "1532635026-d12867005472", "1689942010216-dc412bb1e7a9", "1740914994657-f1cdffdc418e"],
  sop: ["1573166364839-1bfe9196c23e", "1503423571797-2d2bb372094a", "1573167507387-6b4b98cb7c13", "1758518731706-be5d5230e5a5", "1557804506-669a67965ba0", "1512758017271-d7b84c2113f1", "1676276374803-36e48196d5ac", "1556761175-5973dc0f32e7", "1542744173-8e7e53415bb0"],
  procurement: ["1521791136064-7986c2920216", "1681505531034-8d67054e07f6", "1686771416282-3888ddaf249b", "1672380135241-c024f7fbfa13", "1605732562742-3023a888e56e", "1590497008432-598f04441de8", "1678182451047-196f22a4143e", "1670121180530-cfcba4438038"],
  manufacturing: ["1610891015188-5369212db097", "1717386255773-1e3037c81788", "1589793463357-5fb813435467", "1647427060118-4911c9821b82", "1716191299980-a6e8827ba10b", "1528953030358-b0c7de371f1f", "1581092161557-edc82c7a9af0", "1581092334651-ddf26d9a09d0"],
  logistics: ["1791217492296-d0f9b478b888", "1788370538342-b28c755cf61b", "1766785368863-f2188a8c8b32", "1585713181935-d5f622cc2415", "1613690399151-65ea69478674", "1699588999949-e25959a59550", "1565793298595-6a879b1d9492", "1726866672851-5b99c837603c", "1565891741441-64926e441838"],
  governance: ["1682559736721-c2e77ff4c650", "1558494949-ef010cbdcc31", "1506399558188-acca6f8cbf41", "1614064641938-3bbee52942c7", "1614064548237-096f735f344f", "1584433144859-1fc3ab64a957", "1633265486064-086b219458ec"],
  ai: ["1674027444485-cec3da58eef4", "1770233621425-5d9ee7a0a700", "1677442135703-1787eea5ce01", "1753513291124-4f615bf1f6de", "1637002722490-5f8ceed9774c", "1655393001768-d946c97d6fd1", "1716191299980-a6e8827ba10b", "1553413077-190dd305871c", "1526628953301-3e589a6a8b74"],
};
Object.keys(TOPIC_POOLS).forEach((k) => { TOPIC_POOLS[k] = TOPIC_POOLS[k].map((id) => `${u(id)}&h=788`); });
TOPIC_POOLS.general = [...new Set(Object.values(TOPIC_POOLS).flat())];

// First matching rule wins: specific subjects before the generic "AI" one.
const TOPIC_RULES = [
  ["governance", /governance|gouvernance|security|sécurité|cyber|compliance|conformit|regulat|réglement|ai act/],
  ["sop", /s&op|s&oe|ibp|integrated business|sales and operations/],
  ["procurement", /procure|sourcing|achat|supplier|fournisseur|purchas|tariff|douane/],
  ["manufacturing", /manufactur|production|factory|usine|shop.?floor|oee|industri/],
  ["logistics", /logisti|warehous|entrep|transport|freight|fret|shipping|maritime|last.?mile|fleet|carrier/],
  ["inventory", /inventor|stock|replenish|réappro/],
  ["planning", /planning|planification|forecast|prévision|demand|demande|\baps\b/],
  ["ai", /\bai\b|\bia\b|artificial|intelligence|agent|llm|genai|machine learning|copilot/],
];

export const topicOf = (e = {}) => {
  const primary = [e.category, e.relevance_area, ...(Array.isArray(e.tags) ? e.tags : [])].join(" ").toLowerCase();
  for (const text of [primary, String(e.title || "").toLowerCase()]) {
    const hit = TOPIC_RULES.find(([, re]) => re.test(text));
    if (hit) return hit[0];
  }
  return "general";
};

const poolFor = (entity, type) => (type === "news" ? TOPIC_POOLS[topicOf(entity)] : FALLBACK_POOLS[type] || FALLBACK_POOLS.news);
const seedOf = (e = {}) => e.id || e.slug || e.title || "";
const ownImage = (e = {}) =>
  resolveImg([e.image_url, e.featured_image, e.company_logo, e.logo_url].find((x) => x && !isPlaceholderImage(x)) || "");

// Fallback for an entity without its own image: page-assigned `_img`, else themed pick.
export const fallbackForEntity = (entity = {}, type = "news") => {
  if (entity._img) return entity._img;
  const pool = poolFor(entity, type);
  return pool[hashSeed(seedOf(entity)) % pool.length];
};

// Same photo whatever the size/crop params, so dedupe compares photo ids.
const photoKey = (url = "") => (url.match(/photo-[0-9a-z-]+/i) || [url.split("?")[0]])[0];

// Gives every image-less item of a list a distinct pool picture so no photo
// repeats on the page. Own images (and `reserved` image URLs shown elsewhere
// on the page) are claimed first; a repeated own image is replaced.
export const withDistinctImages = (items = [], type = "news", reserved = []) => {
  const used = new Set(reserved.filter(Boolean).map(photoKey));
  const keep = items.map((it) => {
    const own = ownImage(it);
    if (!own || used.has(photoKey(own))) return false;
    used.add(photoKey(own));
    return true;
  });
  return items.map((it, i) => {
    if (keep[i]) return it;
    const start = hashSeed(seedOf(it));
    const pick = [poolFor(it, type), type === "news" ? TOPIC_POOLS.general : []]
      .map((pool) => pool.map((_, k) => pool[(start + k) % pool.length]).find((img) => !used.has(photoKey(img))))
      .find(Boolean) || fallbackForEntity(it, type);
    used.add(photoKey(pick));
    return { ...it, _img: pick };
  });
};

// Pick the primary image for an entity following the priority chain:
//   1. page-assigned `_img` (set by withDistinctImages), else own image (placeholders ignored)
//   2. first linked ecosystem entity logo (if provided via `ecosystemLogo`)
//   3. themed / type fallback pool, seeded by entity id
export const pickEntityImage = (entity = {}, type = "news", opts = {}) => {
  if (entity._img) return entity._img;
  const own = ownImage(entity);
  if (own) return own;
  if (opts.ecosystemLogo) {
    const eco = resolveImg(opts.ecosystemLogo);
    if (eco) return eco;
  }
  return fallbackForEntity(entity, type);
};

// onError handler factory. Falls back type-fallback then global-fallback.
// Uses a data attribute on the element to prevent infinite loops.
// `seed` (optional) keeps fallbacks deterministic per entity.
export const makeImgErrorHandler = (type = "news", seed = "") => (e) => {
  const img = e.currentTarget;
  const stage = img.dataset.fallbackStage || "0";
  if (stage === "0") {
    img.dataset.fallbackStage = "1";
    img.src = fallbackForType(type, seed);
  } else if (stage === "1") {
    img.dataset.fallbackStage = "2";
    img.src = GLOBAL_FALLBACK;
  } else {
    // Final stage: hide the image, leave container background visible.
    img.style.visibility = "hidden";
  }
};
